import { DimensionPickerDialogWithProvider } from '@amzn/gft-dimension-picker';
import { DimSumEnhancedNode } from '@amzn/gft-dimsum-service';
import { KPI_DIMENSION_RENDER_LIST } from '@constants/page';
import { useDeckContext } from '@context/DeckContext';
import { usePageContext } from '@context/PageContext';
import SingleDimensionDialog from '@deckManagement/components/Common/CalculatedKPIBuilder/SingleDimensionDialog';
import {
  generateFormula,
  isSupportedFormula,
  splitRawFormula,
} from '@deckManagement/components/Common/CalculatedKPIBuilder/utils/formulaHelpers';
import EditIcon from '@mui/icons-material/Edit';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { isEmpty, isNil, isNull } from 'lodash';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { PartialKPIDrivers } from 'types/Page.types';
import { CalculatedKPIPanelProps } from 'types/PageSidePanel.types';

const CalculatedKPIPanel = (props: CalculatedKPIPanelProps) => {
  const { initialFormula, initialKpiId, pageDrivers, onFormulaChange, alwaysInclude, disabled = false } = props;
  const [rawFormula, setRawFormula] = useState<string | undefined>('');
  const [localFormulas, setLocalFormulas] = useState<ReturnType<typeof splitRawFormula>>({});
  const [isSupportedCalculatedKpi, setIsSupportedCalculatedKpi] = useState<boolean>(false);
  const [formulaDialogOpen, setFormulaDialogOpen] = useState<boolean>(false);
  const [formulaEditKey, setFormulaEditKey] = useState<string | null>(null);
  const [dimsumDialogOpen, setDimsumDialogOpen] = useState<boolean>(false);
  const [dimsumEditKey, setDimsumEditKey] = useState<string | null>(null);
  const { modifiedPageProperties } = usePageContext();
  const { page_drivers: currentPageKPIDrivers } = modifiedPageProperties || {};
  const { rootNodes } = useDeckContext();

  const ticketTemplateUrl = 'https://t.corp.amazon.com/create/templates/90236c33-68ef-4c65-992a-2159b393c0fc';

  /**
   * Generate non-formula elements for pageDrivers which are not root nodes.
   */
  const nonRootPageDrivers = useCallback(() => {
    const result: ReturnType<typeof splitRawFormula> = {};

    Object.entries(pageDrivers).forEach(([key, driver]) => {
      if (driver?.hierarchy && driver?.value) {
        const nodeId = `${driver.hierarchy}-${driver.value}`;
        const isRootNode = rootNodes?.[key]?.some((node) => node?.nodeID === nodeId);

        if (!isRootNode) {
          result[key] = {
            value: driver.value,
            isFormula: false,
          };
        }
      }
    });

    return result;
  }, [pageDrivers, rootNodes]);

  /**
   * Sets the raw formula when initialFormula changes
   */
  useEffect(() => {
    setRawFormula((prev) => {
      if (prev !== initialFormula) {
        // reset state when raw formula changes
        setLocalFormulas({});
        setFormulaEditKey(null);
        return initialFormula;
      }

      return prev;
    });
  }, [initialFormula]);

  /**
   * Parses the raw formula and sets the localFormulas
   */
  useEffect(() => {
    // Handle new/empty calculated KPI (when kpi_id is '|Calc|' and formula is empty)
    const isNewCalculatedKpi = initialKpiId === '|Calc|' && isEmpty(rawFormula);

    if (rawFormula && !isEmpty(rawFormula)) {
      setLocalFormulas(() => {
        const splitFormula = {
          ...nonRootPageDrivers(),
          ...splitRawFormula(rawFormula, currentPageKPIDrivers as PartialKPIDrivers),
        };
        setIsSupportedCalculatedKpi(isSupportedFormula(rawFormula!));
        return splitFormula;
      });
    } else if (isNewCalculatedKpi) {
      // Initialize empty state for new calculated KPI
      setLocalFormulas({ ...nonRootPageDrivers() });
      setIsSupportedCalculatedKpi(true);
      console.debug('setIsSupportedCalculatedKpi: >TRUE< (new calculated KPI)', initialKpiId);
    } else {
      console.debug('setIsSupportedCalculatedKpi: >FALSE<', rawFormula, !isEmpty(rawFormula));
      setIsSupportedCalculatedKpi(false);
    }
  }, [rawFormula, initialKpiId, nonRootPageDrivers, currentPageKPIDrivers]);

  /**
   * Handles the start of formula editing by tracking the Edit Key
   */
  const handleFormulaEditStart = useCallback((key: string) => {
    setFormulaDialogOpen(true);
    setFormulaEditKey(key);
  }, []);

  /**
   * Handles the start of formula editing by tracking the Edit Key
   */
  const handleDimensionEditStart = useCallback((key: string) => {
    setDimsumEditKey(key);
    setDimsumDialogOpen(true);
  }, []);

  /**
   * Updates local formula state and triggers formula regeneration.
   * @param key - The formula key to update
   * @param newValue - Partial update containing value and/or isFormula flag
   */
  const updateLocalFormulas = useCallback(
    (key: string, newValue: Partial<ReturnType<typeof splitRawFormula>[string]>) => {
      setLocalFormulas((prev) => {
        const newLocalFormulas = {
          ...prev,
          [key]: {
            ...prev[key],
            ...newValue,
          },
        };

        const updatedFormulaData = generateFormula(newLocalFormulas, rootNodes);
        onFormulaChange?.(updatedFormulaData.id, updatedFormulaData.formula);
        return newLocalFormulas;
      });
    },
    [onFormulaChange, rootNodes],
  );

  /**
   * Updates the local formula state when formula editing is confirmed.
   */
  const handleEditConfirm = useCallback(
    (newFormula: string) => {
      if (formulaEditKey) {
        updateLocalFormulas(formulaEditKey, { value: newFormula, isFormula: true });
        setFormulaDialogOpen(false);
      }
    },
    [formulaEditKey, updateLocalFormulas],
  );

  /**
   * Handle when a user confirms the selection of a Dimension Picker value from the DimensionPickerDialog.
   */
  const handleDimensionEditConfirm = useCallback(
    (selectedNodes: DimSumEnhancedNode[]) => {
      if (isNull(dimsumEditKey)) {
        return;
      }

      // DimensionPicker is Single-select only.
      const selectedNode = selectedNodes[0];
      updateLocalFormulas(dimsumEditKey, { value: selectedNode.attributes.name, isFormula: false });
    },
    [dimsumEditKey, updateLocalFormulas],
  );

  /**
   * Render alert component for unsupported KPIs.
   */
  const renderUnsupportedAlert = useCallback(() => {
    return (
      <Alert severity='warning'>
        <Typography variant='body2'>
          To modify this Calculated KPI, please{' '}
          <Link href={ticketTemplateUrl} target='_blank'>
            Submit a Ticket
          </Link>
        </Typography>
      </Alert>
    );
  }, []);

  /**
   * Renders the raw formula (for formulas not supported by the Calculated KPI Builder)
   */
  const renderRawFormula = useCallback(() => {
    // Split the formula and insert <wbr/> elements after math operators
    // Note: <wbr> tag helps provide suggested points for word-wrapping.
    const formulaValue = (!isEmpty(initialKpiId) ? initialKpiId : rawFormula) || '';
    const parts = formulaValue.split(/([+\-*/()|])/);
    const formattedFormula = parts.map((part, index) => {
      if (/[+\-*/()|]/.test(part)) {
        return (
          <Fragment key={`formattedFormula-${index}`}>
            <wbr />
            {part}
          </Fragment>
        );
      }
      return part;
    });

    return (
      <List className='page-side-panel-calculated-kpi segments'>
        {renderUnsupportedAlert()}
        <ListItem disablePadding>
          <ListItemText
            primary={!isEmpty(initialKpiId) ? 'KPI ID' : 'KPI Formula'}
            secondary={formattedFormula}
            slotProps={{ primary: { variant: 'body2' }, secondary: { variant: 'body1' } }}
          />
        </ListItem>
      </List>
    );
  }, [initialKpiId, rawFormula, renderUnsupportedAlert]);

  /**
   * Renders the deckline segments
   */
  const renderKpiSegments = useCallback(() => {
    // Detect if we need to derive the formula dimension: when all explicit dimensions are non-formulas,
    // the missing dimension should be treated as the formula dimension
    const explicitFormulaDimensions = Object.values(localFormulas).filter((entry) => entry.isFormula).length;
    const hasImplicitFormula =
      explicitFormulaDimensions === 0 && (Object.keys(localFormulas).length > 0 || isSupportedCalculatedKpi);

    const renderList = KPI_DIMENSION_RENDER_LIST.filter((dimensionConfig) => {
      const pageDriver = pageDrivers[dimensionConfig.key];
      const formulaKey = dimensionConfig.decklineDrivers?.valueAccessor;

      if (isNil(formulaKey)) {
        return false;
      }

      const formulaEntry = localFormulas[formulaKey];
      const decklineValue = formulaEntry?.value || '';

      // Display if: always included, OR pageDriver has no value (null in page_drivers), OR deckline has non-matching value
      return (
        alwaysInclude?.includes(formulaKey) ||
        !pageDriver?.value ||
        (!isEmpty(decklineValue) && pageDriver.value !== decklineValue)
      );
    });

    // Multiple calculated dimensions is currently not supported.
    const isUnsupported = explicitFormulaDimensions > 1;

    const listItems = renderList
      .map((dimensionConfig, index) => {
        const pageDriver = pageDrivers[dimensionConfig.key];
        const formulaKey = dimensionConfig.decklineDrivers?.valueAccessor;

        if (isNil(formulaKey)) {
          return null;
        }

        const formulaEntry = localFormulas[formulaKey];
        // Treat dimension as formula if explicitly marked OR if it's missing when all others are non-formulas
        const isFormula = formulaEntry?.isFormula || (hasImplicitFormula && !formulaEntry);
        const decklineValue = formulaEntry?.value || '';

        // Do not display segments which are blank or match pageDriver values
        if (
          !alwaysInclude?.includes(formulaKey) &&
          pageDriver?.value &&
          (isEmpty(decklineValue) || pageDriver.value === decklineValue)
        ) {
          return null;
        }

        return (
          <ListItem key={index} disablePadding>
            <ListItemText
              primary={dimensionConfig.label}
              secondary={decklineValue || ''}
              slotProps={{ primary: { variant: 'body2' }, secondary: { variant: 'body1' } }}
            />
            {!isUnsupported && isFormula && (
              <IconButton size='small' onClick={() => handleFormulaEditStart(formulaKey)} disabled={disabled}>
                <EditIcon fontSize='small' />
              </IconButton>
            )}
            {!isUnsupported && !isFormula && (
              <IconButton
                size='small'
                onClick={() => handleDimensionEditStart(formulaKey)}
                disabled={disabled || isUnsupported}
              >
                <EditIcon fontSize='small' />
              </IconButton>
            )}
          </ListItem>
        );
      })
      .filter((item) => !isNil(item));

    return (
      <List className='page-side-panel-calculated-kpi segments'>
        {isUnsupported && renderUnsupportedAlert()}
        {listItems}
      </List>
    );
  }, [
    alwaysInclude,
    disabled,
    handleDimensionEditStart,
    handleFormulaEditStart,
    isSupportedCalculatedKpi,
    localFormulas,
    pageDrivers,
    renderUnsupportedAlert,
  ]);

  /**
   * Handles rendering the Single Dimension Dialog
   */
  const renderSingleDimensionDialog = useCallback(() => {
    if (isNil(formulaEditKey)) {
      return null;
    }

    return (
      <SingleDimensionDialog
        open={formulaDialogOpen}
        onClose={() => setFormulaDialogOpen(false)}
        dimensionName={formulaEditKey}
        dimensionLabel={formulaEditKey}
        formula={localFormulas[formulaEditKey]?.value || ''}
        onFormulaChange={handleEditConfirm}
      />
    );
  }, [formulaDialogOpen, formulaEditKey, handleEditConfirm, localFormulas]);

  /**
   * Handles rendering of the dimension picker
   */
  const renderDimensionPicker = useCallback(() => {
    // TODO: will need hierarchy info for selectedNode to be populated correctly for this Dimension Picker
    // let selectedNode: string[] | undefined;
    // if (!isNull(dimsumEditKey)) {
    //   const data = localFormulas[dimsumEditKey]!;
    //
    //   const nodeIdString = [data.hierarchy, data.value].join('-');
    //
    //   selectedNode = [nodeIdString];
    // }

    if (isNil(dimsumEditKey)) {
      return null;
    }

    return (
      <DimensionPickerDialogWithProvider
        key={`${dimsumEditKey}`}
        open={dimsumDialogOpen && !!dimsumEditKey}
        dimension={dimsumEditKey!}
        title='Dimension Hierarchy'
        onClose={() => {
          setDimsumDialogOpen(false);
          setDimsumEditKey(null);
        }}
        onConfirm={handleDimensionEditConfirm}
      />
    );
  }, [dimsumDialogOpen, dimsumEditKey, handleDimensionEditConfirm]);

  if (Object.values(localFormulas).length === 0 && !isSupportedCalculatedKpi) {
    return null;
  }

  return (
    <Box>
      {isSupportedCalculatedKpi && renderKpiSegments()}
      {!isSupportedCalculatedKpi && renderRawFormula()}
      {renderSingleDimensionDialog()}
      {renderDimensionPicker()}
    </Box>
  );
};

export default CalculatedKPIPanel;
